import type { Story } from '@src/types.ts/story';

export type StoryPlaybackSource = {
  /** Direct MP4, when the story has one. */
  mp4Url?: string;
  /** Mux streaming id, used only when there is no direct MP4. */
  muxPlaybackId?: string;
};

/**
 * Which rendition of the story a player wants.
 *
 * Named after where each chain starts, because Dato's rendition names do not
 * sort the way they look: `mp4UrlHigh` is `res: high`, which is **720p**, while
 * `mp4UrlHd` (1080p) and the bare `mp4Url` (original resolution) are both
 * larger, so the middle chain is named for the rendition it starts at rather
 * than for a rank that would have pointed at the wrong file.
 */
export type PlaybackSourceStrategy =
  /**
   * 720p, then up only if 720p was never generated, then down: what a
   * full-size player wants (`TranscriptVideoPlayer`, `ContentStudio`).
   */
  | '720p-first'
  /**
   * 270p, then up: what a thumbnail-sized player wants. The original upload
   * comes last so a tile never pulls a 4K source.
   */
  | 'lowest-first';

/**
 * Resolve how to play a story's original video.
 *
 * Prefers the direct MP4 — it loads and seeks faster than Mux streaming, which
 * matters for anything that opens parked on a seeked frame — and falls back to
 * the Mux playback id when the story has no MP4 rendition.
 *
 * Pass `'lowest-first'` for anything playing in a row-sized frame. 270p still
 * has ~3x the width of a 96px tile, so nothing is visibly lost, and it is the
 * rendition the rest of Creator Studio already has warm: the editor loads its
 * canvas at `DEFAULT_PREVIEW_VIDEO_RES` (Low) and `RefreshManager` caches
 * `mp4UrlLow` in the renderer, so a tile asking for 720p pulls a second,
 * colder file instead.
 *
 * Deliberately not `ClipPreviewStore.getLowResVideoUrl`, which expresses the
 * same preference for the renderer pre-warm path: it has no Mux fallback, and
 * its last resort is `originalVideo.url` — the raw upload, which is both
 * unbounded in size and missing the audio correction baked into the renditions
 * (`videoResolution.ts`). A preview that exists to be listened to must not land
 * there.
 *
 * (Several players resolve this inline — `TranscriptVideoPlayer`,
 * `ClipRowPreviewPlayer`, `ContentStudio` — and could adopt this helper.)
 */
export function resolveStoryPlaybackSource(
  story: Pick<Story, 'originalVideo'> | null | undefined,
  strategy: PlaybackSourceStrategy = '720p-first',
): StoryPlaybackSource {
  const video = story?.originalVideo?.video;
  const mp4Url = pickMp4Rendition(video, strategy);

  return {
    mp4Url,
    muxPlaybackId: mp4Url ? undefined : video?.muxPlaybackId,
  };
}

/** The first rendition the strategy asks for that the story actually has. */
function pickMp4Rendition(
  video: Story['originalVideo']['video'] | undefined,
  strategy: PlaybackSourceStrategy,
): string | undefined {
  switch (strategy) {
    case 'lowest-first':
      return (
        video?.mp4UrlLow ||
        video?.mp4UrlMedium ||
        video?.mp4UrlHigh ||
        video?.mp4UrlHd ||
        video?.mp4Url
      );
    case '720p-first':
      return (
        video?.mp4UrlHigh ||
        video?.mp4UrlHd ||
        video?.mp4Url ||
        video?.mp4UrlMedium ||
        video?.mp4UrlLow
      );
  }
}
